import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ReactSearchAutocomplete } from 'react-search-autocomplete';

import { ProfileRootState, removeEmptyStrings } from 'utils';
import { filterData, roomData } from '../utils/constants';
import { reactSelectStyles, roomsSelectStyles } from '../utils/reactSelectStyles';

import DropdownInput from './select';
import UserInput from './userInput';

import { RootState } from 'reducers/rootReducer';
import { getSearchResultsAction, getSuggestedLocation, recordPerPage } from 'actions/searchActions';

import 'bootstrap/dist/css/bootstrap.css';

export const INIT_FILTERS = {
	locationId: '',
	radius: '' as number | string,
	objectType: '',
	demandType: '',
	fromPrice: '',
	fromAreaLiving: '',
	fromNumberOfRooms: '',
	recordPerPage: 24,
	zipcode: 0
};

// Mobile-only sensible defaults: pre-select Kaufen + Wohnung and a 15 km
// background radius so the lean phone form returns results immediately.
// Desktop keeps the original empty INIT_FILTERS (no pre-selection). Note:
// INIT_FILTERS is also the base search in header.tsx / finder.tsx, so it MUST
// stay empty there — the mobile defaults live only in this form's state.
const MOBILE_INIT_FILTERS = {
	...INIT_FILTERS,
	radius: 15,
	objectType: 'Apartment',
	demandType: 'Buy'
};
interface ILocationOptions {
	id: string;
	name: string;
	pinCode: string;
}
export const FilterSection = () => {
	const profileDetail = useSelector((state: ProfileRootState) => state.profileDetailReducer);

	// "Is this a phone?" — must match the SCSS `@include phone` definition EXACTLY
	// (portrait ≤576 wide, OR a landscape phone ≤576 tall and ≤960 wide), otherwise
	// the JS that applies the mobile defaults/autofocus disagrees with the CSS that
	// shows the native <select>: a width-only check missed landscape phones, leaving
	// the native control visible while the Kaufen/Wohnung defaults were never set.
	// Kept reactive (matchMedia listener) so a resize/rotation across the boundary
	// stays in sync. Note: a real phone matches in BOTH orientations, so it never
	// flips on rotation — userFilter is therefore initialised once and not rewritten.
	const PHONE_MEDIA_QUERY =
		'(max-width: 576px), (max-height: 576px) and (orientation: landscape) and (max-width: 960px)';

	const [isMobile, setIsMobile] = useState(
		() => typeof window !== 'undefined' && window.matchMedia(PHONE_MEDIA_QUERY).matches
	);

	const [userFilter, setUserFilter] = useState(isMobile ? MOBILE_INIT_FILTERS : INIT_FILTERS);
	// const [locationLabel, setlocationLabel] = useState('');
	const [mainLocationArr, setMainLocationArr] = useState([] as ILocationOptions[]);
	// const [tempLocationArr, setTempLocationArr] = useState([] as any[]);
	// const [searchText, setSearchTxt] = useState('');

	const navigate = useNavigate();
	const dispatch = useDispatch();

	const listFromReducer = useSelector((state: RootState) => state.searchResultReducer);
	const locationData = useSelector((state: any) => state.suggestedLocationReducer);

	const userID = useMemo(() => profileDetail?.data?.userId, [profileDetail]);

	useEffect(() => {
		dispatch(getSuggestedLocation());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Keep isMobile in sync with the viewport (resize/rotation across the boundary).
	useEffect(() => {
		if (typeof window === 'undefined') return;
		const mql = window.matchMedia(PHONE_MEDIA_QUERY);
		const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
		// Safari <14 only supports the deprecated add/removeListener API.
		if (mql.addEventListener) mql.addEventListener('change', onChange);
		else mql.addListener(onChange);
		return () => {
			if (mql.removeEventListener) mql.removeEventListener('change', onChange);
			else mql.removeListener(onChange);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// useEffect(() => {
	// 	setTempLocationArr(mainLocationArr);
	// }, [mainLocationArr]);

	useEffect(() => {
		if (locationData && locationData.status === 'success' && locationData.data) {
			// const mergeDataPinCode = locationData.data.map((item: any) => ({
			// 	id: item.id,
			// 	name: `${item.name}-${item.pinCode}`
			// }));

			setMainLocationArr(locationData.data);
		}
	}, [locationData]);

	useEffect(() => {
		if (listFromReducer && listFromReducer.data && listFromReducer.status === 'success') {
			navigate('/suche');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [listFromReducer]);

	const handleFilterChange = (name: string, value: number) => {
		setUserFilter({
			...userFilter,
			[name]: value
		});
	};

	const changeDropDownValue = (event: any) => {
		let filterValue;
		let filterKey;

		if (Array.isArray(event)) {
			let tempArray: string[] = [];
			event.forEach((option: any) => {
				tempArray.push(option.value);
			});
			filterValue = tempArray;
			filterKey = event[0].id;
		} else {
			filterValue = event.value;
			filterKey = event.id;
		}

		let value = filterValue;

		setUserFilter({
			...userFilter,
			[filterKey]:
				event.id === 'demandType' || event.id === 'objectType'
					? value
					: event.id === 'fromNumberOfRooms'
					? value
					: parseInt(value)
		});
	};

	const setUserFilterData = () => {
		const userSerchData = removeEmptyStrings(userFilter);
		localStorage.setItem('filterData', JSON.stringify(userSerchData));
		// localStorage.setItem(
		//     'filterData',
		//     JSON.stringify({
		//         fromAreaLiving: userFilter.fromAreaLiving,
		//         fromNumberOfRooms: userFilter.fromNumberOfRooms,
		//         fromPrice: userFilter.fromPrice,
		//         locationId: userFilter.locationId,
		//         // locationLabel: locationLabel,
		//         radius: userFilter.radius,
		//         demandType: userFilter.demandType,
		//         objectType: userFilter.objectType,
		//     })
		// );

		dispatch(
			getSearchResultsAction({
				...userSerchData,
				recordPerPage,
				userId: userID
			})
		);
	};

	const userLocation = (event: any) => {
		const zipcode = Number(event.name.match(/\d+/g));
		setUserFilter({
			...userFilter,
			locationId: event.id,
			zipcode
		});
		// setlocationLabel(event.name);
	};

	// const selectResult = (place: any, ref: any) => {
	//   setlocationLabel(ref.value);

	//   setUserFilter({
	//     ...userFilter,
	//     latitude: place?.geometry?.location.lat(),
	//     longitude: place?.geometry?.location.lng(),
	//   });
	// };

	// const handleSearch = (e: any) => {
	// 	return mainLocationArr.filter((item) => item.name.includes(e));
	// };

	const formatResult = (item: any) => {
		return (
			<div className='result-wrapper' key={item.name}>
				<span className='result-span'>{item.name}</span>
			</div>
		);
	};

	// const handleSearch = (searchString: string, results: any[]) => {
	// 	if (!searchString) return results;

	// 	const lowerSearch = searchString.trim().toLowerCase();

	// 	const scoredResults = results.map((item) => {
	// 		const name = item.name.trim().toLowerCase();
	// 		const parts = name.split(',').map((p: any) => p.trim());

	// 		let score = 0;

	// 		if (name === lowerSearch) {
	// 			score = 3; // exact full match
	// 		} else if (parts[0] === lowerSearch) {
	// 			score = 2; // first part matches
	// 		} else if (name.includes(lowerSearch)) {
	// 			score = 1; // partial match
	// 		}

	// 		return { item, score };
	// 	});

	// 	// Sort by score descending
	// 	const sorted = scoredResults
	// 		.filter((r) => r.score > 0)
	// 		.sort((a, b) => b.score - a.score)
	// 		.map((r) => r.item);

	// 	return sorted;
	// };

	return (
		<div className='filter-section'>
			<div className='filter-container'>
				<div className='flex justify__content--center align__items--center city-detail-wrapper'>
					<div className='location-search'>
						<ReactSearchAutocomplete
							items={mainLocationArr}
							onSelect={userLocation}
							// onSearch={handleSearch}
							onClear={() =>
								setUserFilter({
									...userFilter,
									locationId: '',
									radius: isMobile ? 15 : ''
								})
							}
							autoFocus={!isMobile}
							showIcon={false}
							placeholder='Wo? Ort, Straße, PLZ, ...'
							formatResult={formatResult}
							styling={{
								height: '42px',
								fontSize: '16px',
								border: 'none',
								backgroundColor: 'white',
								boxShadow: 'none',
								placeholderColor: '#8B8B8B',
								zIndex: 0
							}}
							maxResults={30}
							showNoResultsText='Keine Ergebnisse'
							className='cursor-pointer'
							// fuseOptions={{
							// 	keys: ['name'],
							// 	threshold: 0.1, // Much stricter matching
							// 	ignoreLocation: true, // Search anywhere in string
							// 	minMatchCharLength: 2, // Minimum 2 chars must match
							// 	distance: 0, // Exact location matching
							// 	findAllMatches: false // Find all potential matches
							// }}
						/>
					</div>
					<div className='flex filter-option'>
						{filterData.map((filter, index) => {
							return (
								<div className={`mobile-field mobile-field--${filter.name}`} key={index}>
									<DropdownInput
										placeholder={filter.placeholder}
										option={filter.options}
										style={reactSelectStyles}
										// Pre-select Kaufen / Wohnung on PHONE only; desktop keeps the
										// original placeholder ("Bitte wählen"). Radius never pre-selected.
										defaultValue={
											filter.name === 'radius' || !isMobile
												? undefined
												: filter.options[0]
										}
										isDisabled={
											filter.name === 'radius' &&
											(!userFilter.locationId || userFilter.locationId === '')
										}
										changeValue={(event: any) =>
											changeDropDownValue(event as Array<any>)
										}
										tooltipTitle={
											filter.name === 'radius' &&
											(!userFilter.locationId || userFilter.locationId === '')
												? 'Bitte wählen Sie den Standort aus'
												: ''
										}
									/>
								</div>
							);
						})}
					</div>
				</div>
				<div className='flex align__items--center user-cost-space-wrapper'>
					<UserInput value={userFilter} handleFilterChange={handleFilterChange} />
					<div className='flex align__items--center justify__content--center  romm-data-wrapper'>
						<p>Zimmer ab </p>
						{roomData.map((room, index) => (
							<DropdownInput
								key={index}
								placeholder={room.placeholder}
								option={room.options}
								style={roomsSelectStyles}
								changeValue={(event: any) => changeDropDownValue(event as Array<any>)}
							/>
						))}
					</div>
				</div>

				<div className='flex justify__content--center align__items--center'>
					<button type='button' className='btn search-btn' onClick={setUserFilterData}>
						Inserate anzeigen
					</button>
				</div>
			</div>
		</div>
	);
};
